/**
 * Exam simulation utility functions.
 *
 * Utility functions for exam simulation calculations and data processing.
 * Includes duration calculation, module filtering, and formatting helpers.
 *
 * @module examSimulation
 */

import type {
  SimulationSettings,
  ExamModule,
} from '@/types/exam-simulation';

/**
 * Calculate total duration for selected modules
 *
 * Calculates the total exam duration based on selected modules, their timings,
 * and pause durations between modules.
 *
 * @param settings - Simulation settings from API
 * @param selectedModules - Array of selected module IDs
 * @returns Total duration in minutes
 *
 * @example
 * ```typescript
 * const duration = calculateTotalDuration(settings, ['reading', 'listening']);
 * // Returns: sum of module timings + pause time
 * ```
 */
export function calculateTotalDuration(
  settings: SimulationSettings,
  selectedModules: ExamModule[],
): number {
  // Filter module order to only include selected modules, preserving order
  const filteredModuleOrder = settings.moduleOrder.filter((module) =>
    selectedModules.includes(module),
  );

  // Calculate total module time
  let totalModuleTime = 0;
  filteredModuleOrder.forEach((module) => {
    const moduleTime = settings.moduleTimings[module] || 0;
    totalModuleTime += moduleTime;
  });

  // Add pause time between modules (one less pause than modules)
  const totalPauseTime =
    filteredModuleOrder.length > 1
      ? (filteredModuleOrder.length - 1) * settings.pauseBetweenModules
      : 0;

  // Total duration = module time + pause time
  return totalModuleTime + totalPauseTime;
}

/**
 * Filter simulation settings based on selected modules
 *
 * Creates a filtered version of simulation settings that only includes
 * selected modules, preserving the order from the original settings.
 *
 * @param settings - Original simulation settings
 * @param selectedModules - Array of selected module IDs
 * @returns Filtered simulation settings
 *
 * @example
 * ```typescript
 * const filtered = filterSettingsByModules(settings, ['reading', 'listening']);
 * // Returns settings with only reading and listening modules
 * ```
 */
export function filterSettingsByModules(
  settings: SimulationSettings,
  selectedModules: ExamModule[],
): {
  moduleOrder: ExamModule[];
  moduleTimings: Record<ExamModule, number>;
  pauseBetweenModules: number;
} {
  // Filter module order to only include selected modules, preserving order
  const filteredModuleOrder = settings.moduleOrder.filter((module) =>
    selectedModules.includes(module),
  );

  // Filter module timings to only include selected modules
  const filteredModuleTimings: Record<ExamModule, number> = {} as Record<
    ExamModule,
    number
  >;
  filteredModuleOrder.forEach((module) => {
    if (settings.moduleTimings[module] !== undefined) {
      filteredModuleTimings[module] = settings.moduleTimings[module];
    }
  });

  return {
    moduleOrder: filteredModuleOrder,
    moduleTimings: filteredModuleTimings,
    pauseBetweenModules: settings.pauseBetweenModules,
  };
}

/**
 * Format duration in minutes to readable string
 *
 * @param minutes - Duration in minutes
 * @returns Formatted duration string (e.g., "95 minutes" or "1h 35min")
 *
 * @example
 * ```typescript
 * formatDuration(95); // Returns "95 minutes"
 * formatDuration(125); // Returns "2h 5min"
 * ```
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} minutes`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }

  return `${hours}h ${remainingMinutes}min`;
}

/**
 * Get human-readable display name for an exam module.
 *
 * Converts module IDs (e.g., 'reading', 'listening') to capitalized display names
 * (e.g., 'Reading', 'Listening').
 *
 * @param module - Module ID (reading, listening, writing, or speaking)
 * @returns Capitalized display name for the module, or the module ID if not recognized
 *
 * @example
 * ```typescript
 * getModuleDisplayName('reading'); // Returns "Reading"
 * getModuleDisplayName('listening'); // Returns "Listening"
 * ```
 */
export function getModuleDisplayName(module: ExamModule): string {
  const names: Record<ExamModule, string> = {
    reading: 'Reading',
    listening: 'Listening',
    writing: 'Writing',
    speaking: 'Speaking',
  };
  return names[module] || module;
}

/**
 * Builds a media source URL from either a direct URL or base64 content.
 *
 * Constructs a data URL from base64 content or returns the direct URL.
 * Used for loading audio and image materials in module introductions.
 *
 * @param mediaUrl - Direct URL to the media (optional)
 * @param base64 - Base64 encoded content (optional)
 * @param mimeType - MIME type of the content (optional)
 * @param defaultMime - Default MIME type if none provided (default: "audio/mpeg")
 * @returns Media source URL or null if neither URL nor base64 is provided
 *
 * @example
 * ```typescript
 * // From URL
 * const src = buildMediaSource('https://example.com/audio.mp3');
 * // Returns: 'https://example.com/audio.mp3'
 *
 * // From base64
 * const src = buildMediaSource(null, 'base64encoded...', 'audio/mpeg');
 * // Returns: 'data:audio/mpeg;base64,base64encoded...'
 * ```
 */
export function buildMediaSource(
  mediaUrl?: string | null,
  base64?: string | null,
  mimeType?: string | null,
  defaultMime: string = "audio/mpeg",
): string | null {
  // Prefer base64 if available (already loaded, no network request needed)
  if (base64) {
    const safeMime = mimeType || defaultMime || "application/octet-stream";
    return `data:${safeMime};base64,${base64}`;
  }
  // Fall back to URL if base64 not available
  if (mediaUrl) {
    // If URL is relative (starts with /), it should work as-is
    // The server should serve /media/... paths directly
    return mediaUrl;
  }
  return null;
}

